import { Provider } from "@angular/core";
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from "@angular/material-moment-adapter";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import { CustomMomentDateAdapter } from "./custom-moment-date-adapter";

enum DateFormatType {
  LONG,
  MEDIUM,
  SHORT,
}

export class DatepickerProvider {
  public static spanish: Provider = {
    provide: MAT_DATE_LOCALE,
    useValue: "es-ES",
  };

  public static adapter: Provider = {
    provide: DateAdapter,
    useClass: MomentDateAdapter,
    deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
  };

  public static customAdapter: Provider = {
    provide: DateAdapter,
    useClass: CustomMomentDateAdapter,
    deps: [MAT_DATE_LOCALE],
  };

  /**
   * Long format: dd/MM/yyyy, hh:mm
   *
   * 1st example:
   * Date not formatted: August 10, 2022
   * Date formatted: 10/08/2022, 08:40
   *
   * 2nd example:
   * Date not formatted: December 2, 2022
   * Date formatted: 02/12/2022, 18:04
   */
  public static long_format: Provider = DatepickerProvider.format(DateFormatType.LONG);

  /**
   * Medium format: dd/MM/yyyy
   *
   * 1st example:
   * Date not formatted: August 10, 2022
   * Date formatted: 10/08/2022
   *
   * 2nd example:
   * Date not formatted: December 2, 2022
   * Date formatted: 02/12/2022
   */
  public static medium_format: Provider = DatepickerProvider.format(DateFormatType.MEDIUM);

  /**
   * Short format: MM/yyyy
   *
   * 1st example:
   * Date not formatted: August 10, 2022
   * Date formatted: 08/2022
   *
   * 2nd example:
   * Date not formatted: December 2, 2022
   * Date formatted: 12/2022
   */
  public static short_format: Provider = DatepickerProvider.format(DateFormatType.SHORT);

  private static format(type: DateFormatType): Provider {
    return {
      provide: MAT_DATE_FORMATS,
      useValue: this.getFormatByType(type),
    };
  }

  private static getFormatByType(type: DateFormatType): DateFormat {
    switch (type) {
      case DateFormatType.LONG:
        return this.longFormat();
      case DateFormatType.SHORT:
        return this.shortFormat();
      case DateFormatType.MEDIUM:
      default:
        return this.mediumFormat();
    }
  }

  private static longFormat(): DateFormat {
    return {
      parse: {
        dateInput: "L, hh:mm",
      },
      display: {
        dateInput: "L, hh:mm",
        monthYearLabel: "MMM YYYY",
        dateA11yLabel: "LL",
        monthYearA11yLabel: "MMMM YYYY",
      },
    };
  }

  private static mediumFormat(): DateFormat {
    return {
      parse: {
        dateInput: "L",
      },
      display: {
        dateInput: "L",
        monthYearLabel: "MMM YYYY",
        dateA11yLabel: "LL",
        monthYearA11yLabel: "MMMM YYYY",
      },
    };
  }

  private static shortFormat(): DateFormat {
    return {
      parse: {
        dateInput: "MM/YYYY",
      },
      display: {
        dateInput: "MM/YYYY",
        monthYearLabel: "MMM YYYY",
        dateA11yLabel: "LL",
        monthYearA11yLabel: "MMMM YYYY",
      },
    };
  }
}

interface DateFormat {
  parse: DateFormatParse;
  display: DateFormatDisplay;
}

interface DateFormatParse {
  dateInput: string;
}

interface DateFormatDisplay {
  dateInput: string;
  monthYearLabel: string;
  dateA11yLabel: string;
  monthYearA11yLabel: string;
}
